import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Loader2 } from 'lucide-react';
import { shipmentService } from '@/services/shipmentService';
import { api } from '@/lib/api';
import type { ShipmentStatus } from '@/types';

const logoImg = '/logo-waigh-transparent.png';

interface Shipment {
  id: number;
  trackingNumber: string;
  senderName: string;
  senderPhone: string;
  senderAddress?: string;
  senderCity?: string;
  receiverName: string;
  receiverPhone: string;
  statusId: number;
  statusName?: string | null;
  statusNameAr?: string | null;
}

export default function EditShipmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState<ShipmentStatus[]>([]);
  const [formData, setFormData] = useState<Shipment>({
    id: 0,
    trackingNumber: '',
    statusId: 1,
    senderName: '',
    senderPhone: '',
    senderAddress: '',
    senderCity: '',
    receiverName: '',
    receiverPhone: ''
  });

  const getStatusLabel = (statusName: string | null | undefined) => {
    const labelMap: Record<string, string> = {
      'Pending': 'شحنة جديدة',
      'In Warehouse': 'في المخزن',
      'In Progress': 'قيد التشغيل',
      'Transferring': 'جاري التحويل',
      'Received at Branch': 'تم استلامها بالفرع',
      'Office Delivery': 'تسليم مكتب',
      'Out for Delivery': 'خارج للتسليم',
      'Delivering': 'جاري التسليم',
      'Delivered': 'تم التسليم',
      'Delivered with Amount Adjustment': 'تسليم بتعديل المبلغ',
      'Partial Delivery': 'تسليم جزئي',
      'Package Exchange': 'طرد مقابل طرد',
      'Package Received': 'استلام طرد',
      'Returning': 'قيد الاسترجاع',
      'Refused with Shipping Fees': 'رفض الاستلام مع سداد مصروفات الشحن',
      'Refused without Shipping Fees': 'رفض الاستلام دون سداد مصروفات الشحن',
      'Amount Remitted': 'تم توريد المبلغ للراسل',
      'Postponed': 'تأجيل الاستلام',
      'No Answer After Postponement': 'لا يرد بعد التأجيل',
      'No Answer': 'لا يرد',
      'Phone Closed': 'الهاتف مغلق',
      'Unavailable': 'غير متاح',
      'Cancelled': 'إلغاء الشحنة',
      'Returned to Sender Notification': 'مرتجع بعلم الراسل',
      'Returned to Sender': 'تم إرجاع الشحنة للراسل',
      'Redirected': 'تحويل إلى عنوان آخر',
      'Wrong Address': 'عنوان خاطئ',
      'Unclear Address': 'عنوان غير واضح',
      'Invalid Phone Number': 'رقم الهاتف غير صحيح',
      'Out of Service Area': 'خارج نطاق التغطية',
      'Forbidden Shipment': 'ممنوع الشحن',
      'Damaged Shipment': 'شحنة تالفة',
      'Lost Shipment': 'شحنة مفقودة',
      'InTransit': 'جاري التحويل (In Transit)',
      'Returned': 'تم إرجاع الشحنة (Returned)'
    };
    return labelMap[statusName || ''] || statusName || "غير محدد";
  };

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const statusRes = await shipmentService.getStatuses();
        let activeStatuses: ShipmentStatus[] = [];
        if (statusRes.success && statusRes.data) {
          activeStatuses = statusRes.data;
          setStatuses(activeStatuses);
        }

        const res = await api.get(`/api/shipments/${id}`);
        const payload = res.data?.data ?? res.data;
        const data = payload?.shipment ?? payload;
        if (res.data?.success || data) {
          
          if (data) {
            // معالجة ذكية جداً لـ StatusId لضمان اختيار الحالة الصحيحة من الـ Dropdown
            let finalStatusId = data.statusId || data.statusNameId;
            if (!finalStatusId && (data.status || data.statusName)) {
              const currentStatusStr = data.status || data.statusName;
              const matched = activeStatuses.find(s => s.name.toLowerCase() === currentStatusStr.toLowerCase());
              if (matched) finalStatusId = matched.id;
            }

            // فحص كافّة مسميات الحقول المتوقعة من الباكيند وملئها تلقائياً
            setFormData({
              id: data.id || 0,
              trackingNumber: data.trackingNumber || data.code || '',
              statusId: finalStatusId || 1,
              statusName: data.statusName || data.status || data.status?.name || '',
              statusNameAr: data.statusNameAr || data.status?.nameAr || data.status?.NameAr || '',
              senderName: data.senderName || data.clientName || (data.sender && data.sender.name) || '',
              senderPhone: data.senderPhone || data.clientPhone || data.sender?.phone || '',
              senderAddress: data.senderAddress || data.merchantAddress || data.sender?.address || '',
              senderCity: data.senderCity || data.merchantCity || data.sender?.city || '',
              receiverName: data.receiverName || data.recipientName || '',
              receiverPhone: data.receiverPhone || data.recipientPhone || ''
            });
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.put(`/api/shipments/${id}`, formData);
      
      if (response.data?.success) {
        alert('تم تحديث الشحنة بنجاح!');
        navigate('/shipments');
      } else {
        alert(response.data?.message || 'حدث خطأ أثناء الحفظ بالسيرفر');
      }
    } catch (err) {
      alert('خطأ في الاتصال بالسيرفر');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center gap-3 font-bold text-slate-600" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <Loader2 className="animate-spin text-orange-500" size={32} />
        <span>جاري جلب البيانات والتوافق التلقائي مع الحقول...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto text-right" dir="rtl" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <div className="flex justify-between items-center mb-6 border-b border-orange-100 pb-4">
        <div className="flex items-center gap-3">
          <img src={logoImg} alt="Target Express" className="h-12 w-auto object-contain" />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-orange-500" /> تعديل الشحنة
          </h1>
        </div>
        <button type="button" onClick={() => navigate('/shipments')} className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
          <ArrowLeft className="w-4 h-4" /> عودة لقائمة الشحنات
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-orange-100 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">رقم البوليصة (لا يمكن تعديله)</label>
            <input type="text" disabled className="w-full p-2.5 border border-orange-200 rounded-lg bg-orange-50 text-orange-700 font-mono font-bold" value={formData.trackingNumber} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">الحالة اللوجستية الحالية</label>
            {user?.role === 'merchant' ? (
              <div className="w-full p-2.5 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600 font-bold cursor-not-allowed select-none">
                {formData.statusNameAr || formData.statusName || 'غير محدد'}
              </div>
            ) : (
              <select 
                className="w-full p-2.5 border border-orange-200 rounded-lg bg-white outline-none font-bold text-sm text-orange-800 focus:border-orange-400 focus:ring-1 focus:ring-orange-200" 
                value={formData.statusId} 
                onChange={e => {
                  const selectedId = Number(e.target.value);
                  setFormData(prev => ({ ...prev, statusId: selectedId }));
                }}
              >
                {statuses.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.nameAr || status.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h2 className="text-sm font-bold text-orange-600 mb-3">بيانات المرسل الأساسية</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">اسم الراسل</label>
              <input type="text" required className="w-full p-2.5 border border-slate-200 rounded-lg outline-none text-sm font-semibold" value={formData.senderName} onChange={e => setFormData({...formData, senderName: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">رقم هاتف الراسل</label>
              <input type="tel" required className="w-full p-2.5 border border-slate-200 rounded-lg outline-none text-sm text-left font-mono font-semibold" value={formData.senderPhone} onChange={e => setFormData({...formData, senderPhone: e.target.value})} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-bold text-orange-700 mb-1.5">عنوان التاجر الثابت</label>
            <input
              type="text"
              readOnly
              value={formData.senderAddress || 'غير مسجل'}
              className="w-full p-2.5 border border-orange-200 rounded-lg bg-orange-50 text-orange-800 text-sm cursor-not-allowed font-semibold"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-orange-700 mb-1.5">مدينة أو محافظة التاجر</label>
            <input
              type="text"
              readOnly
              value={formData.senderCity || 'غير مسجلة'}
              className="w-full p-2.5 border border-orange-200 rounded-lg bg-orange-50 text-orange-800 text-sm cursor-not-allowed font-semibold"
            />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h2 className="text-sm font-bold text-orange-600 mb-3">بيانات المرسل إليه (المستلم)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">اسم المستلم</label>
              <input type="text" required className="w-full p-2.5 border border-slate-200 rounded-lg outline-none text-sm font-semibold" value={formData.receiverName} onChange={e => setFormData({...formData, receiverName: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">رقم هاتف المستلم</label>
              <input type="tel" required className="w-full p-2.5 border border-slate-200 rounded-lg outline-none text-sm text-left font-mono font-semibold" value={formData.receiverPhone} onChange={e => setFormData({...formData, receiverPhone: e.target.value})} />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-50">
          <button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs px-6 py-3 rounded-xl transition-colors shadow-sm disabled:opacity-50">
            {saving ? 'جاري حفظ التعديلات اللوجستية...' : 'حفظ التغييرات والبوليصة'}
          </button>
        </div>
      </form>
    </div>
  );
}
